import { color } from '@nuvolo/nuux-theme/utils';
import styled, { css } from 'styled-components';

/* ── Shared line ─────────────────────────────────────────────────────────── */
export const HR = styled.hr`
  width: 100%;
  border: 0;
  border-top: 1px solid ${color('primary', 'light3')};
`;

/* ── “Contact / Logout” option row (desktop vs mobile) ───────────────────── */
/** desktop: 14px, 4px margin, 8px gap; mobile: 16px, 16px margin, 16px gap */
export const OptionButton = styled.div<{ size?: 'sm' | 'md' }>`
  cursor: pointer;
  color: ${color('custom', 'contrastText')};
  font-weight: ${({ size }) => (size === 'sm' ? 500 : 400)};
  font-size: 14px;
  line-height: 20px;
  margin: ${({ size }) => (size === 'sm' ? '6px 0' : '8px 0')};
  display: flex;
  align-items: center;
  gap: ${({ size }) => (size === 'sm' ? '8px' : '16px')};

  &:has(.logo-wrapper) {
    margin: 0;
  }

  &:hover {
    .item-text {
      text-decoration: underline;  }
`;

/* ── Overlays ────────────────────────────────────────────────────────────── */
export const Overlay = styled.div<{ z?: number; bg?: string }>`
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  z-index: ${({ z = 1000 }) => z};
  background: ${({ bg = 'rgba(0,0,0,0.3)' }) => bg};
`;

/** convenience alias for mobile “backdrop” use-cases */
export const Backdrop = styled(Overlay).attrs({ z: 900 })``;

/* ── Profile trigger (desktop uses absolute; mobile static) ──────────────── */
export const ProfileButton = styled.div<{ active?: boolean; isDesktop?: boolean }>`
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  width: 108px;
  height: 74px;

  ${({ isDesktop }) =>
    isDesktop
      ? css`
          position: absolute;
          bottom: 16px;
        `
      : css`
          position: static;
        `}

  background:  ${({ active }) => (active ? color('custom', 'hoverColor') : 'transparent')};

  &:hover {
    background: ${color('custom', 'hoverColor')};
  }
`;
/* ── Application picker trigger (desktop uses absolute; mobile static) ──────────────── */
export const ApplicationPicker = styled(ProfileButton)`
  position: static;
  ${({ isDesktop }) =>
    !isDesktop &&
    css`
      width: 24px;
      height: 24px;
    `}
`;

/* ── Circular avatar (desktop 42px; mobile 40px) ─────────────────────────── */
export const IconCircle = styled.div<{ size?: number }>`
  background: linear-gradient(180deg, #fe9801 0%, #f4312a 100%);
  border-radius: 100px;
  color: ${color('contrast', 'light')};
  width: ${({ size = 42 }) => size}px;
  height: ${({ size = 42 }) => size}px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: ${({ size = 42 }) => Math.max(12, Math.floor(size * 0.45))}px;
  font-weight: 500;
  user-select: none;
`;

/* ── The profile/details and application picker card (desktop vs mobile) ────────────────────────── */
/** desktop: fixed size/position; mobile: full width, ~35vh, anchored bottom */
export const Card = styled.div<{
  variant: 'desktop' | 'mobile';
  type?: 'appPicker' | 'profile';
  w?: number;
  h?: number;
}>`
  background: ${color('custom', 'cardBackground')};
  border: 1px solid ${color('primary', 'light3')};
  box-shadow: var(--elevation-lvl3);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  z-index: 9998;
  div {
    color: ${color('custom', 'contrastText')};
  }

  ${({ type = 'profile', w = 200 }) =>
    type === 'profile'
      ? css`
          padding: 16px;
          border-radius: 16px;
          bottom: 15px;
        `
      : css`
          padding: 24px;
          border-radius: 32px;
          div {
            margin: 8px 0;
            &.section-end {
              padding-bottom: 16px;
              border-bottom: 1px solid ${color('primary', 'light3')};
              width: 100%;
            }
          }
          i {
            margin-right: 12px;
            padding: 8px;
            border-radius: 10px;
            background: ${color('custom', 'iconGray', 0.05)};
            width: 36px;
            height: 36px;
          }
        `}

  ${({ variant, type = 'profile', w = 200 }) =>
    variant === 'desktop'
      ? css`
          position: absolute;
          left: 75px;
          ${type === 'appPicker' ? 'top: 10px;' : ''}
          width: ${w}px;
          height: auto;
        `
      : css`
          position: absolute;
          left: 0;
          bottom: 0;
          width: 100%;
          border-radius: 12px 12px 0 0;
        `};

  .profile-menu-label {
    line-height: 16px;
    font-size: 12px;
    font-weight: 400;
    color: ${color('custom', 'labelGray')};
    opacity: 1;
  }
  .profile-inactive-items {
    text-align: left;
    line-height: 20px;
    color: #7f7f7f;
    margin: 6px 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 176px;
  }
`;

export const SimplePopupCard = styled.div<{
  w?: string;
  h?: string;
}>`
  padding: 16px;
  background-color: ${color('custom', 'dataGridBackground')};
  border-radius: 12px;
  border: 1px solid ${color('custom', 'formInputBorder')};
  box-shadow: var(--elevation-lvl3);

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;

  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 1100;
  transform: translate(-50%, -50%);

  height: ${({ h = 'auto' }) => h};
  width: ${({ w = 'auto' }) => (w === 'auto' ? w : `calc(${w} - 16px)`)};
`;

/* ── Small “X” button used on mobile card ────────────────────────────────── */
export const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${color('custom', 'iconGray')};
  z-index: 9999;

  &:hover {
    color: ${color('contrast', 'dark')};
  }
  i:before {
    line-height: 1 !important;
  }
`;

export const MoreHeaderRowActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 24px;
`;

export const TenantsContainer = styled.div`
  .tenant-item {
    border: 1px solid ${color('ui', 'light2')};
    border-radius: 24px;
    padding: 20px 24px;
    margin-bottom: 16px;
    align-items: center;
    display: flex;
    gap: 16px;
    font-size: 14px;
    .icon-wrapper {
      width: 40px;
      height: 40px;
      background-color: ${color('info', 'light2')};
      border-radius: 12px;
      justify-content: center;
      display: flex;
      align-items: center;
    }
    .tenant-icon {
      color: ${color('info', 'light')};
    }
  }
`;
